import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Warehouse, Plus, Search, Trash2, Edit2, Loader2, Package,
  ArrowRightLeft, TrendingUp, TrendingDown, Scale, Eye, Check,
  ChevronsUpDown, Filter, Upload, FileText, Download, FileCheck, Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import ArmazemForm from "../components/armazens/ArmazemForm";
import EstoqueGraoForm from "../components/armazens/EstoqueGraoForm";
import MovimentacaoGraoForm from "../components/armazens/MovimentacaoGraoForm";
import Paginacao from "../components/shared/Paginacao";
import ExportacaoRelatorios from "@/components/financeiro/ExportacaoRelatorios";
import { imprimirTicketGrao } from "../components/armazens/TicketGraoImpressao";

const produtosConfig = {
  soja: { label: "Soja", color: "bg-green-100 text-green-700", icon: "🌱" },
  milho: { label: "Milho", color: "bg-yellow-100 text-yellow-700", icon: "🌽" },
  sorgo: { label: "Sorgo", color: "bg-orange-100 text-orange-700", icon: "🌾" },
  trigo: { label: "Trigo", color: "bg-amber-100 text-amber-700", icon: "🌾" },
  feijao: { label: "Feijão", color: "bg-red-100 text-red-700", icon: "🫘" },
  arroz: { label: "Arroz", color: "bg-stone-100 text-stone-700", icon: "🍚" },
  cafe: { label: "Café", color: "bg-brown-100 text-amber-900", icon: "☕" },
  algodao: { label: "Algodão", color: "bg-blue-100 text-blue-700", icon: "🏵️" },
  amendoim: { label: "Amendoim", color: "bg-yellow-100 text-yellow-800", icon: "🥜" },
  girassol: { label: "Girassol", color: "bg-yellow-100 text-yellow-600", icon: "🌻" },
  outro: { label: "Outro", color: "bg-stone-100 text-stone-700", icon: "📦" }
};

const tiposArmazem = {
  silo_proprio: "Silo Próprio",
  silo_terceiro: "Silo Terceiro",
  armazem_proprio: "Armazém Próprio",
  armazem_terceiro: "Armazém Terceiro",
  bags: "Big Bags",
  tulha: "Tulha"
};

const META_TAG = "META_CONTRATO:";

const parseObservacoesContrato = (obs) => {
  const raw = String(obs || "");
  const linhas = raw.split("\n");
  const idx = linhas.findIndex((l) => l.startsWith(META_TAG));
  let meta = {};
  if (idx >= 0) {
    const json = linhas[idx].slice(META_TAG.length).trim();
    try {
      meta = JSON.parse(json);
    } catch { }
    linhas.splice(idx, 1);
  }
  return { texto: linhas.join("\n").trim(), meta };
};

const buildObservacoesContrato = (texto, meta) => {
  const linhas = [];
  if ((texto || "").trim()) linhas.push(texto.trim());
  if (meta && Object.keys(meta).length) linhas.push(`${META_TAG}${JSON.stringify(meta)}`);
  return linhas.join("\n");
};

const parseNumero = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).replace(/\./g, "").replace(",", ".");
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : null;
};

const formatDecimal = (value, decimals = 2) => {
  const num = parseNumero(value);
  if (num === null) return "";
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

export default function ArmazensGraos() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const navigate = useNavigate();
  const [armazemDialogOpen, setArmazemDialogOpen] = useState(false);
  const [estoqueDialogOpen, setEstoqueDialogOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editingArmazem, setEditingArmazem] = useState(null);
  const [selectedArmazem, setSelectedArmazem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProduto, setFilterProduto] = useState("todos");
  const [filterDataInicio, setFilterDataInicio] = useState("");
  const [filterDataFim, setFilterDataFim] = useState("");
  const [activeTab, setActiveTab] = useState("armazens");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const getInitialContractForm = () => ({
    codigo: "",
    tipo_contrato: "compra_venda",
    operacao: "venda",
    produto: "soja",
    comprador: "",
    comprador_id: "",
    quantidade_total: "",
    unidade: "sacas",
    preco_contrato: "",
    data_contrato: new Date().toISOString().split('T')[0],
    data_inicio: new Date().toISOString().split('T')[0],
    data_fim: "",
    tarifa_armazenagem: "",
    armazem_id: "",
    origem: "",
    destino: "",
    transportadora: "",
    transportadora_id: "",
    valor_frete: "",
    prazo_entrega: "",
    termos: "",
    clausulas: "",
    condicoes: "",
    observacoes: "",
    anexos: []
  });
  const [contractForm, setContractForm] = useState(getInitialContractForm);
  const [contractErrors, setContractErrors] = useState({});
  const [contractSearch, setContractSearch] = useState("");
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerTipoFiltro, setPartnerTipoFiltro] = useState("todos");
  const [partnerEstadoFiltro, setPartnerEstadoFiltro] = useState("todos");
  const [partnerHistoricoFiltro, setPartnerHistoricoFiltro] = useState("todos");
  const [contractDraftLoaded, setContractDraftLoaded] = useState(false);
  const [relatorioFiltro, setRelatorioFiltro] = useState({
    tipo: "todos",
    produto: "todos",
    status: "todos",
    cliente: "",
    inicio: "",
    fim: ""
  });

  const queryClient = useQueryClient();

  const { data: armazens = [], isLoading } = useQuery({
    queryKey: ['armazens', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Armazem.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: estoques = [] } = useQuery({
    queryKey: ['estoques_grao', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.EstoqueGrao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: estoqueBalanco = [] } = useQuery({
    queryKey: ['estoque_balanco', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.EstoqueBalanco.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: movimentacoes = [] } = useQuery({
    queryKey: ['movimentacoes_grao', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.MovimentacaoGrao.filter({ propriedade_id: propriedadeId }, '-data', 200)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasReceberAll = [] } = useQuery({
    queryKey: ['contas_receber', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaReceber.filter({ propriedade_id: propriedadeId }, '-data_vencimento', 500)
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: parceiros = [] } = useQuery({
    queryKey: ['parceiros', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Parceiro.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: talhoes = [] } = useQuery({
    queryKey: ['talhoes', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Talhao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: propriedadeData } = useQuery({
    queryKey: ['propriedade_ticket', propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.Propriedade.get(propriedadeId) : Promise.resolve(null),
    enabled: !!propriedadeId
  });

  const { data: contratosReais = [] } = useQuery({
    queryKey: ['contratos_comercializacao', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContratoComercializacao.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const deleteArmazemMutation = useMutation({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['armazens'] })
  });

  const deleteMovementMutation = useMutation({
    mutationFn: async (mov) => {
      // O estoque agora é calculado em tempo real via View SQL.
      // Sendo assim, basta deletar a movimentação e o saldo será recalculado automaticamente pela View.
      // Se houver contas vinculadas, o usuário deve ser avisado (ou podemos deletar aqui se for o caso).
      return agrogestao.entities.MovimentacaoGrao.delete(mov.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['movimentacoes_grao'] });
      queryClient.invalidateQueries({ queryKey: ['estoque_balanco'] });
      toast.success("Movimentação excluída e estoque atualizado!");
    },
    onError: (err) => toast.error(`Erro ao excluir: ${err.message}`)
  });

  const formatNumber = (value, decimals = 0) => {
    return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: decimals }).format(value || 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const contratoCategorias = useMemo(() => ([
    'Venda de Grãos (Contrato)',
    'Contrato de Grãos',
    'Contrato de Armazenagem',
    'Contrato de Transporte'
  ]), []);

  const contratosBase = useMemo(() => {
    return (contasReceberAll || []).filter(c => contratoCategorias.includes(c.categoria));
  }, [contasReceberAll, contratoCategorias]);

  const contratosComercializacao = useMemo(() => {
    return (contratosReais || []).map(c => {
      const entregueKg = (movimentacoes || [])
        .filter(m => m.contrato_id === c.id || (m.observacoes || '').includes(`contrato_id:${c.id}`))
        .reduce((sum, m) => sum + (parseFloat(m.peso_liquido || m.quantidade) || 0), 0);

      // Convert to same unit as contract
      let entregue = entregueKg;
      if (c.unidade === 'sacas') entregue = entregueKg / 60;
      else if (c.unidade === 'ton') entregue = entregueKg / 1000;

      return {
        ...c,
        isReal: true,
        contrato_meta: {
          comprador: c.cliente,
          comprador_id: null, // If using names
          produto: c.produto,
          quantidade_total: c.quantidade,
          unidade: c.unidade,
          preco_contrato: c.preco,
          data_contrato: c.data_contrato,
        },
        contrato_codigo: c.id.substring(0, 8).toUpperCase(),
        contrato_produto: c.produto,
        contrato_quantidade: c.quantidade,
        contrato_entregue: entregue,
        contrato_preco: c.preco,
        contrato_tipo: "compra_venda",
        cliente: c.cliente,
        categoria: "Contrato Comercial"
      };
    });
  }, [contratosReais, movimentacoes]);

  const contratosEnriquecidos = useMemo(() => {
    const simulados = contratosBase.map((ct) => {
      const { texto, meta } = parseObservacoesContrato(ct.observacoes);
      const desc = ct.descricao || '';
      const codMatch = desc.match(/Contrato\s+([^\s•]+)/i);
      const qtdMatch = desc.match(/Qtde\s+([\d.,]+)/i);
      const precoMatch = desc.match(/Preço\s+([\d.,]+)/i);
      const produtoMatch = desc.match(/Contrato.*?•\s*([^\s•]+)\s*•/i);
      const codigo = meta.codigo || codMatch?.[1] || '';
      const produto = meta.produto || produtoMatch?.[1] || '';
      const qtdContratada = meta.quantidade_total ?? (qtdMatch ? parseNumero(qtdMatch[1]) : null);
      const precoContrato = meta.preco_contrato ?? (precoMatch ? parseNumero(precoMatch[1]) : null);
      const tipoContrato = meta.tipo_contrato || "compra_venda";
      const entregueKg = (movimentacoes || [])
        .filter(m => m.contrato_id === ct.id || (m.observacoes || '').includes(`contrato_id:${ct.id}`))
        .reduce((sum, m) => sum + (parseFloat(m.peso_liquido || m.quantidade) || 0), 0);

      let entregue = entregueKg;
      const unity = meta.unidade || 'sacas';
      if (unity === 'sacas') entregue = entregueKg / 60;
      else if (unity === 'ton') entregue = entregueKg / 1000;

      return {
        ...ct,
        isReal: false,
        contrato_meta: meta || {},
        contrato_obs_texto: texto,
        contrato_codigo: codigo,
        contrato_produto: produto,
        contrato_quantidade: qtdContratada,
        contrato_entregue: entregue,
        contrato_preco: precoContrato,
        contrato_tipo: tipoContrato
      };
    });
    return [...contratosComercializacao, ...simulados];
  }, [contratosBase, contratosComercializacao, movimentacoes]);

  const contratosPorCliente = useMemo(() => {
    const mapa = {};
    contratosEnriquecidos.forEach((ct) => {
      const key = ct.contrato_meta?.comprador_id || ct.cliente || ct.contrato_meta?.comprador || ct.contrato_meta?.contratante || '';
      if (!key) return;
      mapa[key] = (mapa[key] || 0) + 1;
    });
    return mapa;
  }, [contratosEnriquecidos]);

  const estadosParceiros = useMemo(() => {
    const estados = new Set();
    parceiros.forEach((p) => {
      if (p.estado) estados.add(String(p.estado).toUpperCase());
    });
    return Array.from(estados).sort();
  }, [parceiros]);

  const parceirosFiltrados = useMemo(() => {
    const base = parceiros.filter((p) => {
      const tipo = p.tipo || "outro";
      if (partnerTipoFiltro !== "todos" && tipo !== partnerTipoFiltro) return false;
      if (partnerEstadoFiltro !== "todos" && String(p.estado || "").toUpperCase() !== partnerEstadoFiltro) return false;
      if (partnerHistoricoFiltro !== "todos") {
        const key = p.id || (p.nome_razao || p.razao_social || p.nome || p.cnpj_cpf || "").trim();
        const count = contratosPorCliente[key] || contratosPorCliente[p.nome_razao] || contratosPorCliente[p.razao_social] || contratosPorCliente[p.nome] || 0;
        if (partnerHistoricoFiltro === "com" && count === 0) return false;
        if (partnerHistoricoFiltro === "sem" && count > 0) return false;
      }
      return true;
    });
    if (!partnerSearch) return base;
    const s = partnerSearch.toLowerCase();
    return base.filter((p) => {
      const nome = (p.nome_razao || p.razao_social || p.nome || "").toLowerCase();
      const doc = (p.cnpj_cpf || "").toLowerCase();
      const email = (p.email || "").toLowerCase();
      const cidade = (p.cidade || "").toLowerCase();
      const estado = (p.estado || "").toLowerCase();
      return nome.includes(s) || doc.includes(s) || email.includes(s) || cidade.includes(s) || estado.includes(s);
    });
  }, [parceiros, partnerTipoFiltro, partnerEstadoFiltro, partnerHistoricoFiltro, partnerSearch, contratosPorCliente]);

  const parceiroSelecionado = useMemo(() => {
    const id = contractForm.tipo_contrato === "transporte" ? contractForm.transportadora_id : contractForm.comprador_id;
    if (id) {
      const byId = parceiros.find((p) => p.id === id);
      if (byId) return byId;
    }
    const nome = contractForm.tipo_contrato === "transporte" ? contractForm.transportadora : contractForm.comprador;
    if (!nome) return null;
    const normalizado = String(nome).trim();
    return parceiros.find((p) => {
      const label = (p.nome_razao || p.razao_social || p.nome || p.cnpj_cpf || "").trim();
      return label && label === normalizado;
    }) || null;
  }, [
    parceiros,
    contractForm.comprador,
    contractForm.comprador_id,
    contractForm.transportadora,
    contractForm.transportadora_id,
    contractForm.tipo_contrato
  ]);

  const kpis = useMemo(() => {
    const totalCapacidade = armazens.reduce((a, arm) => a + (arm.capacidade_total || 0), 0);

    // Normalize quantity to KG first to handle legacy mixed units
    const getKg = (item) => {
      // Usar o saldo real calculado pela View se disponível para o par armazem/produto
      const itemBalanco = estoqueBalanco.find(b => b.armazem_id === item.armazem_id && b.produto === item.produto);
      if (itemBalanco) return parseFloat(itemBalanco.quantidade_real) || 0;

      const q = parseFloat(item.quantidade) || 0;
      if (item.unidade === 'sacas') return q * 60;
      if (item.unidade === 'toneladas') return q * 1000;
      return q; // defaults to kg
    };

    const totalEstoqueKg = estoques.reduce((a, e) => a + getKg(e), 0);
    const totalEstoqueSc = totalEstoqueKg / 60;

    const valorEstoque = estoques.reduce((a, e) => {
      const isKg = e.unidade === 'kg';
      const itemBalanco = estoqueBalanco.find(b => b.armazem_id === e.armazem_id && b.produto === e.produto);
      const qtdSc = itemBalanco ? (itemBalanco.quantidade_real / 60) : (isKg ? (e.quantidade || 0) / 60 : (e.quantidade || 0));
      const custoSc = (isKg && (e.custo_medio || 0) < 10) ? (e.custo_medio || 0) * 60 : (e.custo_medio || 0);
      return a + (qtdSc * custoSc);
    }, 0);

    const estoquePorProduto = {};
    estoques.forEach(e => {
      if (!estoquePorProduto[e.produto]) {
        estoquePorProduto[e.produto] = 0;
      }
      estoquePorProduto[e.produto] += getKg(e) / 60; // Convert normalized KG to SC for display
    });

    return {
      totalArmazens: armazens.length,
      totalCapacidade,
      totalEstoque: totalEstoqueSc,
      ocupacao: totalCapacidade > 0 ? (totalEstoqueSc / totalCapacidade) * 100 : 0,
      valorEstoque,
      estoquePorProduto
    };
  }, [armazens, estoques]);

  // Filtros para movimentações
  const movimentacoesFiltradas = useMemo(() => {
    let filtered = movimentacoes;

    if (filterProduto !== "todos") {
      filtered = filtered.filter(m => m.produto === filterProduto);
    }

    if (filterDataInicio) {
      filtered = filtered.filter(m => m.data >= filterDataInicio);
    }

    if (filterDataFim) {
      filtered = filtered.filter(m => m.data <= filterDataFim);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (term.startsWith('contrato_id:')) {
        const cid = term.split(':')[1];
        filtered = filtered.filter(m =>
          m.contrato_id === cid || (m.observacoes || '').includes(`contrato_id:${cid}`)
        );
      } else {
        filtered = filtered.filter(m =>
          m.origem_destino?.toLowerCase().includes(term) ||
          m.nota_fiscal?.toLowerCase().includes(term) ||
          m.observacoes?.toLowerCase().includes(term)
        );
      }
    }

    return filtered.sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
  }, [movimentacoes, filterProduto, searchTerm, filterDataInicio, filterDataFim]);

  // Paginação
  const movimentacoesPaginadas = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return movimentacoesFiltradas.slice(start, start + pageSize);
  }, [movimentacoesFiltradas, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterProduto, filterDataInicio, filterDataFim]);

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['armazens'] });
    queryClient.invalidateQueries({ queryKey: ['estoques_grao'] });
    queryClient.invalidateQueries({ queryKey: ['movimentacoes_grao'] });
    setArmazemDialogOpen(false);
    setEstoqueDialogOpen(false);
    setEditingArmazem(null);
    setSelectedArmazem(null);
  };

  const contratoDraftKey = useMemo(() => (
    propriedadeId ? `contrato_rascunho_${propriedadeId}` : "contrato_rascunho"
  ), [propriedadeId]);

  useEffect(() => {
    if (contractDialogOpen) return;
    setContractDraftLoaded(false);
  }, [contractDialogOpen]);

  useEffect(() => {
    if (!contractDialogOpen || editingContract || contractDraftLoaded) return;
    try {
      const raw = localStorage.getItem(contratoDraftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setContractForm((prev) => ({ ...getInitialContractForm(), ...parsed, anexos: parsed.anexos || [] }));
      }
    } catch { }
    setContractDraftLoaded(true);
  }, [contractDialogOpen, editingContract, contractDraftLoaded, contratoDraftKey]);

  useEffect(() => {
    if (!contractDialogOpen || editingContract) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(contratoDraftKey, JSON.stringify(contractForm));
      } catch { }
    }, 400);
    return () => clearTimeout(timer);
  }, [contractForm, contractDialogOpen, editingContract, contratoDraftKey]);

  useEffect(() => {
    if (contractForm.tipo_contrato === "transporte") {
      setPartnerTipoFiltro("transportador");
      return;
    }
    setPartnerTipoFiltro("cliente");
  }, [contractForm.tipo_contrato]);

  const resetContractForm = () => {
    setContractForm(getInitialContractForm());
    setContractErrors({});
  };

  const getTipoContratoLabel = (tipo) => {
    if (tipo === "armazenagem") return "Armazenagem";
    if (tipo === "transporte") return "Transporte";
    return "Compra/Venda";
  };

  const buildContractDescricao = (form) => {
    const codigo = form.codigo ? `Contrato ${form.codigo}` : "Contrato";
    if (form.tipo_contrato === "armazenagem") {
      const produto = form.produto ? ` • ${form.produto}` : "";
      const qtd = form.quantidade_total ? ` • Qtde ${form.quantidade_total}` : "";
      return `${codigo} • Armazenagem${produto}${qtd}`.trim();
    }
    if (form.tipo_contrato === "transporte") {
      const produto = form.produto ? ` • ${form.produto}` : "";
      const rota = form.origem || form.destino ? ` • ${form.origem || "-"} → ${form.destino || "-"}` : "";
      return `${codigo} • Transporte${produto}${rota}`.trim();
    }
    const produto = form.produto ? ` • ${form.produto}` : "";
    const qtd = form.quantidade_total ? ` • Qtde ${form.quantidade_total}` : "";
    const preco = form.preco_contrato ? ` • Preço ${form.preco_contrato}` : "";
    return `${codigo}${produto}${qtd}${preco}`.trim();
  };

  const buildContractValor = (form) => {
    if (form.tipo_contrato === "armazenagem") {
      const tarifa = parseNumero(form.tarifa_armazenagem);
      const qtd = parseNumero(form.quantidade_total);
      if (tarifa && qtd) return tarifa * qtd;
      return tarifa || 0;
    }
    if (form.tipo_contrato === "transporte") {
      return parseNumero(form.valor_frete) || 0;
    }
    const qtd = parseNumero(form.quantidade_total);
    const preco = parseNumero(form.preco_contrato);
    if (!qtd || !preco) return 0;
    return qtd * preco;
  };

  const handleDecimalBlur = (field, decimals = 2) => {
    setContractForm((prev) => ({ ...prev, [field]: formatDecimal(prev[field], decimals) }));
  };

  const validateContractForm = (form) => {
    const errors = {};
    if (!form.codigo) errors.codigo = "Obrigatório";
    if (!form.tipo_contrato) errors.tipo_contrato = "Obrigatório";
    if (!form.produto) errors.produto = "Obrigatório";
    if (form.tipo_contrato === "compra_venda") {
      if (!form.operacao) errors.operacao = "Obrigatório";
      if (!form.comprador) errors.comprador = "Obrigatório";
      if (!parseNumero(form.quantidade_total) || parseNumero(form.quantidade_total) <= 0) errors.quantidade_total = "Informe a quantidade";
      if (!parseNumero(form.preco_contrato) || parseNumero(form.preco_contrato) <= 0) errors.preco_contrato = "Informe o preço";
      if (!form.data_contrato) errors.data_contrato = "Informe a data";
    }
    if (form.tipo_contrato === "armazenagem") {
      if (!form.comprador) errors.comprador = "Obrigatório";
      if (!parseNumero(form.quantidade_total) || parseNumero(form.quantidade_total) <= 0) errors.quantidade_total = "Informe a quantidade";
      if (!parseNumero(form.tarifa_armazenagem) || parseNumero(form.tarifa_armazenagem) <= 0) errors.tarifa_armazenagem = "Informe a tarifa";
      if (!form.armazem_id) errors.armazem_id = "Selecione o armazém";
      if (!form.data_inicio) errors.data_inicio = "Informe a data de início";
    }
    if (form.tipo_contrato === "transporte") {
      if (!form.transportadora) errors.transportadora = "Obrigatório";
      if (!form.origem) errors.origem = "Informe a origem";
      if (!form.destino) errors.destino = "Informe o destino";
      if (!parseNumero(form.valor_frete) || parseNumero(form.valor_frete) <= 0) errors.valor_frete = "Informe o valor do frete";
    }
    return errors;
  };

  const isContratoFinalizado = (ct) => {
    const qtdContratada = ct.contrato_quantidade || 0;
    const qtdEntregue = ct.isReal
      ? (ct.contrato_entregue || 0)
      : movimentacoes.filter(m => m.tipo === 'saida' && (m.observacoes || '').includes(`contrato_id:${ct.id}`))
        .reduce((a, m) => a + (m.quantidade || 0), 0);

    const total = ct.valor || (ct.isReal ? (ct.quantidade * ct.preco) : 0);
    const entregue = ct.isReal
      ? (ct.contrato_entregue || 0) * (ct.contrato_preco || 0)
      : (contasReceberAll || []).filter(e => e.categoria === 'Venda de Grãos' && (e.observacoes || '').includes(`contrato_id:${ct.id}`))
        .reduce((a, e) => a + (e.valor || 0), 0);

    return (ct.isReal ? ct.status === 'encerrado' : (ct.contrato_meta?.status_contrato === 'encerrado' || (ct.observacoes || '').includes('finalizado:1')))
      || (qtdContratada > 0 && qtdEntregue >= qtdContratada)
      || (total > 0 && entregue >= total);
  };

  const handleSelectParceiro = (p) => {
    const nome = (p.nome_razao || p.razao_social || p.nome || p.cnpj_cpf || "").trim();
    setContractForm((prev) => {
      if (prev.tipo_contrato === "transporte") {
        return { ...prev, transportadora: nome, transportadora_id: p.id || "" };
      }
      return { ...prev, comprador: nome, comprador_id: p.id || "" };
    });
    setPartnerOpen(false);
  };

  const handleUploadContratoAnexo = async (file) => {
    if (!file || !editingContract) return;
    const path = `contratos/${propriedadeId}/${editingContract.id}/${Date.now()}_${file.name}`;
    const res = await agrogestao.integrations.Core.UploadPrivateFile({ file, path });
    const next = [
      ...(contractForm.anexos || []),
      { nome: file.name, url: res.path, tipo: file.type, tamanho: file.size, data: new Date().toISOString() }
    ];
    setContractForm((prev) => ({ ...prev, anexos: next }));
  };

  const handleOpenAnexo = async (doc) => {
    try {
      const { signed_url } = await agrogestao.integrations.Core.CreateFileSignedUrl({ path: doc.url });
      if (signed_url) window.open(signed_url, "_blank");
    } catch { }
  };

  const contratoHistorico = useMemo(() => {
    if (!editingContract) return [];
    const { meta } = parseObservacoesContrato(editingContract.observacoes);
    return Array.isArray(meta?.historico) ? meta.historico : [];
  }, [editingContract]);

  const contratosRelatorio = useMemo(() => {
    const base = contratosEnriquecidos.filter((ct) => {
      if (relatorioFiltro.tipo !== "todos" && ct.contrato_tipo !== relatorioFiltro.tipo) return false;
      if (relatorioFiltro.produto !== "todos" && ct.contrato_produto !== relatorioFiltro.produto) return false;
      if (relatorioFiltro.status === "ativos" && isContratoFinalizado(ct)) return false;
      if (relatorioFiltro.status === "encerrados" && !isContratoFinalizado(ct)) return false;
      if (relatorioFiltro.cliente) {
        const nome = (ct.cliente || ct.contrato_meta?.comprador || ct.contrato_meta?.transportadora || "").toLowerCase();
        if (!nome.includes(relatorioFiltro.cliente.toLowerCase())) return false;
      }
      if (relatorioFiltro.inicio || relatorioFiltro.fim) {
        const data = ct.data_vencimento || ct.contrato_meta?.data_contrato || ct.contrato_meta?.data_inicio;
        if (data) {
          const dt = new Date(data);
          if (relatorioFiltro.inicio && dt < new Date(relatorioFiltro.inicio)) return false;
          if (relatorioFiltro.fim && dt > new Date(relatorioFiltro.fim)) return false;
        }
      }
      return true;
    });
    return base;
  }, [contratosEnriquecidos, relatorioFiltro, isContratoFinalizado]);

  const colunasRelatorio = useMemo(() => ([
    { label: "Código", accessor: (c) => c.contrato_codigo || c.id },
    { label: "Tipo", accessor: (c) => getTipoContratoLabel(c.contrato_tipo) },
    { label: "Produto", accessor: (c) => c.contrato_produto || "-" },
    { label: "Contratante", accessor: (c) => c.cliente || c.contrato_meta?.comprador || c.contrato_meta?.transportadora || "-" },
    { label: "Quantidade", accessor: (c) => c.contrato_quantidade || 0 },
    { label: "Unidade", accessor: (c) => c.contrato_meta?.unidade || "-" },
    { label: "Valor", accessor: (c) => c.valor || 0, type: "currency" },
    { label: "Data", accessor: (c) => c.data_vencimento || c.contrato_meta?.data_contrato || c.contrato_meta?.data_inicio, type: "date" },
    { label: "Status", accessor: (c) => (isContratoFinalizado(c) ? "Encerrado" : "Ativo") }
  ]), [isContratoFinalizado]);

  const getOcupacaoArmazem = (armazemId) => {
    const estoqueArmazem = estoques
      .filter(e => e.armazem_id === armazemId)
      .reduce((a, e) => a + (e.quantidade || 0), 0);
    const armazem = armazens.find(a => a.id === armazemId);
    if (!armazem?.capacidade_total) return 0;
    return (estoqueArmazem / armazem.capacidade_total) * 100;
  };

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <Warehouse className="w-8 h-8 text-amber-600" />
            Armazéns e Estoque de Grãos
          </h1>
          <p className="text-stone-600 mt-1">Controle de armazenagem e movimentação de grãos</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setArmazemDialogOpen(true)} variant="outline" className="gap-2">
            <Plus className="w-4 h-4" />
            Novo Armazém
          </Button>
          <Button onClick={() => navigate('/MovimentacaoGrao')} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Nova Movimentação
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <p className="text-amber-100 text-sm">Armazéns</p>
            <p className="text-2xl font-bold mt-1">{kpis.totalArmazens}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <p className="text-blue-100 text-sm">Capacidade Total</p>
            <p className="text-xl font-bold mt-1">{formatNumber(kpis.totalCapacidade)} sc</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-green-100 text-sm">Estoque Atual</p>
            <p className="text-xl font-bold mt-1">{formatNumber(kpis.totalEstoque)} sc</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <p className="text-purple-100 text-sm">Ocupação</p>
            <p className="text-2xl font-bold mt-1">{kpis.ocupacao.toFixed(1)}%</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <p className="text-indigo-100 text-sm">Valor Estoque</p>
            <p className="text-lg font-bold mt-1">{formatCurrency(kpis.valorEstoque)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Estoque por Produto */}
      {Object.keys(kpis.estoquePorProduto).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(kpis.estoquePorProduto).map(([produto, qtd]) => (
            <Badge key={produto} className={`${produtosConfig[produto]?.color} text-sm py-1 px-3`}>
              {produtosConfig[produto]?.icon} {produtosConfig[produto]?.label}: {formatNumber(qtd)} sc
            </Badge>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1">
          <TabsTrigger value="armazens" className="gap-2">
            <Warehouse className="w-4 h-4" />
            Armazéns
          </TabsTrigger>
          <TabsTrigger value="estoques" className="gap-2">
            <Package className="w-4 h-4" />
            Estoques
          </TabsTrigger>
          <TabsTrigger value="movimentacoes" className="gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Movimentações
          </TabsTrigger>
          <TabsTrigger value="contratos" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Contratos
          </TabsTrigger>
        </TabsList>

        {/* Armazéns */}
        <TabsContent value="armazens" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {armazens.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Warehouse className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhum armazém cadastrado</p>
              </div>
            ) : (
              armazens.map(armazem => {
                const ocupacao = getOcupacaoArmazem(armazem.id);
                const estoqueArmazem = estoques.filter(e => e.armazem_id === armazem.id);

                return (
                  <Card key={armazem.id} className="bg-white border-stone-200">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{armazem.nome}</CardTitle>
                          <Badge variant="secondary" className="mt-1">
                            {tiposArmazem[armazem.tipo] || armazem.tipo}
                          </Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingArmazem(armazem); setArmazemDialogOpen(true); }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteArmazemMutation.mutate(armazem.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span>Capacidade: {formatNumber(armazem.capacidade_total)} {armazem.unidade_capacidade}</span>
                        <span className={ocupacao > 90 ? 'text-red-600 font-medium' : ''}>
                          {ocupacao.toFixed(1)}%
                        </span>
                      </div>
                      <Progress value={ocupacao} className="h-2" />

                      {estoqueArmazem.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {estoqueArmazem.map(e => {
                            const balanco = estoqueBalanco.find(b => b.armazem_id === armazem.id && b.produto === e.produto);
                            const qtdRealKg = balanco ? balanco.quantidade_real : (e.unidade === 'sacas' ? e.quantidade * 60 : e.quantidade);
                            const qtdSc = qtdRealKg / 60;
                            return (
                              <Badge key={e.id} className={`${produtosConfig[e.produto]?.color} text-xs`}>
                                {produtosConfig[e.produto]?.icon} {formatNumber(qtdSc)} sc
                              </Badge>
                            );
                          })}
                        </div>
                      )}

                      {armazem.localizacao && (
                        <p className="text-xs text-stone-500">📍 {armazem.localizacao}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Estoques */}
        <TabsContent value="estoques" className="mt-6">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">Posição de Estoque</h3>
            </div>

            {estoques.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhum estoque registrado</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left py-3 px-4">Produto</th>
                    <th className="text-left py-3 px-4">Armazém</th>
                    <th className="text-right py-3 px-4">Quantidade</th>
                    <th className="text-right py-3 px-4">Umidade</th>
                    <th className="text-right py-3 px-4">Custo Médio</th>
                    <th className="text-right py-3 px-4">Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {estoqueBalanco.map(eb => {
                    const e = estoques.find(est => est.armazem_id === eb.armazem_id && est.produto === eb.produto) || {
                      id: eb.id,
                      produto: eb.produto,
                      armazem_id: eb.armazem_id,
                      unidade: 'kg',
                      custo_medio: 0
                    };
                    const armazem = armazens.find(a => a.id === eb.armazem_id);
                    const isKg = e.unidade === 'kg';
                    const qtdSc = eb.quantidade_real / 60;
                    const custoSc = (isKg && (e.custo_medio || 0) < 10) ? (e.custo_medio || 0) * 60 : (e.custo_medio || 0);

                    return (
                      <tr key={eb.id} className="border-t">
                        <td className="py-3 px-4">
                          <Badge className={produtosConfig[eb.produto]?.color}>
                            {produtosConfig[eb.produto]?.icon} {produtosConfig[eb.produto]?.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">{armazem?.nome || '-'}</td>
                        <td className="py-3 px-4 text-right font-medium">
                          {formatNumber(qtdSc)} sc
                        </td>
                        <td className="py-3 px-4 text-right">{e.umidade ? `${e.umidade}%` : '-'}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(custoSc)}/sc</td>
                        <td className="py-3 px-4 text-right font-medium text-green-600">
                          {formatCurrency(qtdSc * custoSc)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/* Movimentações */}
        <TabsContent value="movimentacoes" className="mt-6 space-y-4">
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  placeholder="Buscar por origem/destino ou NF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={filterDataInicio}
                  onChange={(e) => setFilterDataInicio(e.target.value)}
                  className="w-[140px]"
                  title="Data Inicial"
                />
                <span className="text-stone-400">até</span>
                <Input
                  type="date"
                  value={filterDataFim}
                  onChange={(e) => setFilterDataFim(e.target.value)}
                  className="w-[140px]"
                  title="Data Final"
                />
              </div>
              <Select value={filterProduto} onValueChange={setFilterProduto}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Produto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {Object.entries(produtosConfig).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.icon} {val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {searchTerm.startsWith('contrato_id:') && (
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                <FileCheck className="w-4 h-4" />
                Filtrando entregas vinculadas ao contrato
              </div>
              <Button size="xs" variant="ghost" className="text-blue-600 hover:bg-blue-100 h-7" onClick={() => setSearchTerm('')}>
                Limpar Filtro
              </Button>
            </div>
          )}

          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            {movimentacoesFiltradas.length === 0 ? (
              <div className="p-12 text-center">
                <ArrowRightLeft className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhuma movimentação encontrada</p>
              </div>
            ) : (
              <>
                <div className="divide-y">
                  {movimentacoesPaginadas.map(mov => {
                    const armazem = armazens.find(a => a.id === mov.armazem_id);
                    const isEntrada = mov.tipo === 'entrada';

                    return (
                      <div key={mov.id} className="p-4 hover:bg-stone-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEntrada ? 'bg-green-100' : 'bg-red-100'}`}>
                              {isEntrada ? (
                                <TrendingUp className="w-5 h-5 text-green-600" />
                              ) : (
                                <TrendingDown className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className={produtosConfig[mov.produto]?.color}>
                                  {produtosConfig[mov.produto]?.icon} {produtosConfig[mov.produto]?.label}
                                </Badge>
                                <Badge variant="outline">{mov.tipo}</Badge>
                              </div>
                              <p className="text-sm text-stone-500 mt-1">
                                {armazem?.nome} • {mov.origem_destino}
                                {mov.nota_fiscal && ` • NF: ${mov.nota_fiscal}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className={`font-bold ${isEntrada ? 'text-green-600' : 'text-red-600'}`}>
                                {isEntrada ? '+' : '-'}{formatNumber(mov.unidade === 'kg' ? (mov.quantidade || 0) / 60 : mov.quantidade)} sc
                              </p>
                              <p className="text-sm text-stone-500">
                                {format(parseISO(mov.data), "dd/MM/yyyy")}
                              </p>
                              {mov.valor_total > 0 && (
                                <p className="text-sm font-medium">{formatCurrency(mov.valor_total)}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/MovimentacaoGrao?id=${mov.id}`);
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const safra = safras.find(s => s.id === mov.safra_id);
                                  const talhao = talhoes.find(t => t.id === mov.talhao_id);

                                  // Find the ticket number by index in the sorted list (approx)
                                  const idx = movimentacoes.length - (movimentacoes.findIndex(m => m.id === mov.id) || 0);

                                  imprimirTicketGrao({
                                    ...mov,
                                    propriedade: propriedadeData,
                                    armazem: armazem,
                                    safra_nome: safra?.nome,
                                    talhao_nome: talhao?.nome || talhao?.descricao,
                                    numero_ticket: idx
                                  });
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Reimprimir Ticket"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (confirm("Tem certeza que deseja excluir esta movimentação? O estoque será recalculado automaticamente.")) {
                                    deleteMovementMutation.mutate(mov);
                                  }
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Paginacao
                  totalItems={movimentacoesFiltradas.length}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                />
              </>
            )}
          </div>
        </TabsContent>

        {/* Contratos */}
        <TabsContent value="contratos" className="mt-6">
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="p-4 border-b flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <h3 className="font-semibold">Contratos de Grãos</h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <Input
                    placeholder="Buscar por código/cliente..."
                    value={contractSearch}
                    onChange={(e) => setContractSearch(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
                <Button onClick={() => setContractDialogOpen(true)} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-1" /> Novo Contrato
                </Button>
                <Button onClick={() => navigate('/MovimentacaoGrao')} size="sm" className="bg-[#1B4332] hover:bg-[#2D6A4F]">
                  <ArrowRightLeft className="w-4 h-4 mr-1" /> Nova Entrega
                </Button>
              </div>
            </div>
            {(() => {
              const contratos = contractSearch
                ? contratosEnriquecidos.filter(c => {
                  const term = contractSearch.toLowerCase();
                  return (c.descricao || '').toLowerCase().includes(term)
                    || (c.cliente || '').toLowerCase().includes(term)
                    || (c.contrato_codigo || '').toLowerCase().includes(term)
                    || (c.contrato_produto || '').toLowerCase().includes(term);
                })
                : contratosEnriquecidos;
              const entregas = (contasReceberAll || []).filter(c => c.categoria === 'Venda de Grãos');
              if (contratos.length === 0) {
                return (
                  <div className="p-12 text-center">
                    <Package className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-500">Nenhum contrato cadastrado</p>
                  </div>
                );
              }
              return (
                <div className="divide-y">
                  {contratos.map((ct) => {
                    const totalValor = ct.valor || (ct.quantidade && ct.preco ? ct.quantidade * ct.preco : 0);
                    const entregueValor = ct.isReal
                      ? (ct.contrato_entregue || 0) * (ct.contrato_preco || 0)
                      : (contasReceberAll || [])
                        .filter(e => e.categoria === 'Venda de Grãos' && (e.observacoes || '').includes(`contrato_id:${ct.id}`))
                        .reduce((a, e) => a + (e.valor || 0), 0);

                    const restanteValor = Math.max(0, totalValor - entregueValor);
                    const progressValor = totalValor > 0 ? (entregueValor / totalValor) * 100 : 0;

                    const qtdContratada = ct.contrato_quantidade || 0;
                    const qtdEntregue = ct.contrato_entregue || 0;

                    const progressQtd = qtdContratada > 0 ? (qtdEntregue / qtdContratada) * 100 : 0;
                    const finalizado = ct.isReal ? ct.status === 'encerrado' : isContratoFinalizado(ct);

                    const contratanteNome = ct.cliente || ct.contrato_meta?.comprador || ct.contrato_meta?.transportadora || '';
                    const contratanteLabel = ct.contrato_tipo === "transporte" ? "Transportadora" : "Contratante";

                    return (
                      <div key={ct.id} className={`p-4 hover:bg-stone-50 ${ct.isReal ? 'border-l-4 border-blue-500' : ''}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant={ct.isReal ? "default" : "secondary"}>
                                {ct.isReal ? "Contrato Real" : "Financeiro Simul."}
                              </Badge>
                              <Badge variant="outline">{getTipoContratoLabel(ct.contrato_tipo)}</Badge>
                              <Badge variant="outline" className={finalizado ? "bg-stone-100 text-stone-500" : "bg-emerald-50 text-emerald-700"}>
                                {finalizado ? 'encerrado' : (ct.status || 'ativo')}
                              </Badge>
                            </div>
                            <p className="font-medium text-stone-800 mt-1 truncate">{ct.isReal ? `Contrato #${ct.contrato_codigo}` : ct.descricao}</p>
                            <p className="text-sm text-stone-500">
                              {contratanteNome ? `${contratanteLabel}: ${contratanteNome}` : `${contratanteLabel}: -`}
                              {ct.data_vencimento ? ` • Entrega: ${format(parseISO(ct.data_vencimento), "dd/MM/yyyy")}` : ''}
                              {ct.contrato_produto ? ` • Produto: ${ct.contrato_produto}` : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-stone-500">Valor Estimado</p>
                            <p className="text-lg font-semibold">{formatCurrency(totalValor)}</p>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-stone-100">
                          <div>
                            <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                              <span>Entrega Física ({ct.contrato_meta?.unidade || 'kg'})</span>
                              <span className="font-mono">{formatNumber(qtdEntregue, 1)} / {formatNumber(qtdContratada, 0)}</span>
                            </div>
                            <Progress value={progressQtd} className="h-1.5" />
                          </div>
                          <div>
                            <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                              <span>Progresso Financeiro</span>
                              <span className="font-mono">{progressValor.toFixed(0)}%</span>
                            </div>
                            <Progress value={progressValor} className="h-1.5 bg-stone-100" />
                          </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {!ct.isReal && (
                            <Button variant="outline" size="sm" onClick={() => {
                              const meta = ct.contrato_meta || {};
                              const tipoContrato = meta.tipo_contrato || "compra_venda";
                              setContractForm({
                                ...getInitialContractForm(),
                                codigo: meta.codigo || ct.contrato_codigo,
                                tipo_contrato: tipoContrato,
                                operacao: meta.operacao || "venda",
                                produto: meta.produto || ct.contrato_produto || "soja",
                                comprador: ct.cliente || meta.comprador || "",
                                comprador_id: meta.comprador_id || "",
                                quantidade_total: String(meta.quantidade_total || ""),
                                unidade: meta.unidade || "sacas",
                                preco_contrato: String(meta.preco_contrato || ""),
                                data_contrato: meta.data_contrato || ct.data_vencimento || new Date().toISOString().split('T')[0],
                                data_inicio: meta.data_inicio || new Date().toISOString().split('T')[0],
                                data_fim: meta.data_fim || "",
                                tarifa_armazenagem: String(meta.tarifa_armazenagem || ""),
                                armazem_id: meta.armazem_id || "",
                                origem: meta.origem || "",
                                destino: meta.destino || "",
                                transportadora: meta.transportadora || "",
                                transportadora_id: meta.transportadora_id || "",
                                valor_frete: String(meta.valor_frete || ""),
                                prazo_entrega: meta.prazo_entrega || "",
                                termos: meta.termos || "",
                                clausulas: meta.clausulas || "",
                                condicoes: meta.condicoes || "",
                                observacoes: ct.contrato_obs_texto || "",
                                anexos: meta.anexos || []
                              });
                              setEditingContract(ct);
                              setContractDialogOpen(true);
                            }}>
                              Editar
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => {
                            setActiveTab('movimentacoes');
                            setFilterProduto('todos');
                            setSearchTerm(ct.isReal ? `contrato_id:${ct.id}` : `contrato_id:${ct.id}`);
                          }}>
                            Ver Entregas
                          </Button>
                          {!finalizado && !ct.isReal && (
                            <Button size="sm" variant="ghost" className="text-stone-500 hover:text-red-600" onClick={async () => {
                              if (!confirm("Encerrar contrato financeiro?")) return;
                              const { texto, meta } = parseObservacoesContrato(ct.observacoes);
                              const base = texto.includes("finalizado:1") ? texto : `${texto}${texto ? "\n" : ""}finalizado:1`;
                              const nextMeta = { ...meta, status_contrato: "encerrado" };
                              await agrogestao.entities.ContaReceber.update(ct.id, { observacoes: buildObservacoesContrato(base, nextMeta) });
                              queryClient.invalidateQueries({ queryKey: ['contas_receber'] });
                            }}>
                              Encerrar
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <div className="mt-6 bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="p-4 border-b flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h4 className="font-semibold">Relatórios de Contratos</h4>
                <p className="text-xs text-stone-500">Exportação personalizada por filtros</p>
              </div>
              <ExportacaoRelatorios
                dados={contratosRelatorio}
                nomeArquivo="Relatorio_Contratos_Graos"
                colunas={colunasRelatorio}
              />
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
              <Select value={relatorioFiltro.tipo} onValueChange={(v) => setRelatorioFiltro((prev) => ({ ...prev, tipo: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os tipos</SelectItem>
                  <SelectItem value="compra_venda">Compra/Venda</SelectItem>
                  <SelectItem value="armazenagem">Armazenagem</SelectItem>
                  <SelectItem value="transporte">Transporte</SelectItem>
                </SelectContent>
              </Select>
              <Select value={relatorioFiltro.produto} onValueChange={(v) => setRelatorioFiltro((prev) => ({ ...prev, produto: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Produto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os produtos</SelectItem>
                  {Object.keys(produtosConfig).map((key) => (
                    <SelectItem key={key} value={key}>{produtosConfig[key].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={relatorioFiltro.status} onValueChange={(v) => setRelatorioFiltro((prev) => ({ ...prev, status: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="ativos">Ativos</SelectItem>
                  <SelectItem value="encerrados">Encerrados</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Cliente/Transportadora"
                value={relatorioFiltro.cliente}
                onChange={(e) => setRelatorioFiltro((prev) => ({ ...prev, cliente: e.target.value }))}
              />
              <Input
                type="date"
                value={relatorioFiltro.inicio}
                onChange={(e) => setRelatorioFiltro((prev) => ({ ...prev, inicio: e.target.value }))}
              />
              <Input
                type="date"
                value={relatorioFiltro.fim}
                onChange={(e) => setRelatorioFiltro((prev) => ({ ...prev, fim: e.target.value }))}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={armazemDialogOpen} onOpenChange={setArmazemDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Warehouse className="w-5 h-5 text-amber-600" />
              {editingArmazem ? "Editar Armazém" : "Novo Armazém"}
            </DialogTitle>
            <DialogDescription>
              Cadastre ou edite informações do armazém
            </DialogDescription>
          </DialogHeader>
          <ArmazemForm
            armazem={editingArmazem}
            onSuccess={handleSuccess}
            onCancel={() => { setArmazemDialogOpen(false); setEditingArmazem(null); }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={estoqueDialogOpen} onOpenChange={setEstoqueDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              Adicionar Estoque
            </DialogTitle>
            <DialogDescription>
              Registre entrada de grãos e custo médio
            </DialogDescription>
          </DialogHeader>
          <EstoqueGraoForm
            armazens={armazens}
            safras={safras}
            onSuccess={handleSuccess}
            onCancel={() => setEstoqueDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>



      <Dialog open={contractDialogOpen} onOpenChange={(v) => { setContractDialogOpen(v); if (!v) { setEditingContract(null); resetContractForm(); } }}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContract ? "Editar Contrato" : "Novo Contrato"}</DialogTitle>
            <DialogDescription>
              {editingContract ? "Atualize os dados e anexe documentos" : "Cadastre contratos de compra, armazenagem ou transporte"}
            </DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="dados">
            <TabsList className="bg-stone-100 p-1">
              <TabsTrigger value="dados">Dados</TabsTrigger>
              <TabsTrigger value="termos">Termos</TabsTrigger>
              <TabsTrigger value="anexos">Anexos</TabsTrigger>
              <TabsTrigger value="historico" disabled={!editingContract}>Histórico</TabsTrigger>
            </TabsList>
            <TabsContent value="dados" className="mt-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Identificação</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Código *</Label>
                    <Input
                      value={contractForm.codigo}
                      onChange={(e) => setContractForm({ ...contractForm, codigo: e.target.value })}
                      placeholder="Ex: CV-2025-001"
                      className={contractErrors.codigo ? "border-red-500" : ""}
                    />
                    {contractErrors.codigo && <p className="text-xs text-red-500">{contractErrors.codigo}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select value={contractForm.tipo_contrato} onValueChange={(v) => setContractForm({ ...contractForm, tipo_contrato: v })}>
                      <SelectTrigger className={contractErrors.tipo_contrato ? "border-red-500" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="compra_venda">Compra/Venda</SelectItem>
                        <SelectItem value="armazenagem">Armazenagem</SelectItem>
                        <SelectItem value="transporte">Transporte</SelectItem>
                      </SelectContent>
                    </Select>
                    {contractErrors.tipo_contrato && <p className="text-xs text-red-500">{contractErrors.tipo_contrato}</p>}
                  </div>
                  {contractForm.tipo_contrato === "compra_venda" && (
                    <div className="space-y-2">
                      <Label>Operação *</Label>
                      <Select value={contractForm.operacao} onValueChange={(v) => setContractForm({ ...contractForm, operacao: v })}>
                        <SelectTrigger className={contractErrors.operacao ? "border-red-500" : ""}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="compra">Compra</SelectItem>
                          <SelectItem value="venda">Venda</SelectItem>
                        </SelectContent>
                      </Select>
                      {contractErrors.operacao && <p className="text-xs text-red-500">{contractErrors.operacao}</p>}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Data do Contrato</Label>
                    <Input
                      type="date"
                      value={contractForm.data_contrato}
                      onChange={(e) => setContractForm({ ...contractForm, data_contrato: e.target.value })}
                      className={contractErrors.data_contrato ? "border-red-500" : ""}
                    />
                    {contractErrors.data_contrato && <p className="text-xs text-red-500">{contractErrors.data_contrato}</p>}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Contratante</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Select value={partnerTipoFiltro} onValueChange={setPartnerTipoFiltro}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os tipos</SelectItem>
                        <SelectItem value="cliente">Clientes</SelectItem>
                        <SelectItem value="fornecedor">Fornecedores</SelectItem>
                        <SelectItem value="transportador">Transportadores</SelectItem>
                        <SelectItem value="outro">Outros</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={partnerEstadoFiltro} onValueChange={setPartnerEstadoFiltro}>
                      <SelectTrigger>
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos os estados</SelectItem>
                        {estadosParceiros.map((uf) => (
                          <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={partnerHistoricoFiltro} onValueChange={setPartnerHistoricoFiltro}>
                      <SelectTrigger>
                        <SelectValue placeholder="Histórico" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Com/Sem contratos</SelectItem>
                        <SelectItem value="com">Com contratos</SelectItem>
                        <SelectItem value="sem">Sem contratos</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Buscar parceiro"
                      value={partnerSearch}
                      onChange={(e) => setPartnerSearch(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{contractForm.tipo_contrato === "transporte" ? "Transportadora *" : contractForm.tipo_contrato === "armazenagem" ? "Contratante *" : "Comprador *"}</Label>
                    <Popover open={partnerOpen} onOpenChange={setPartnerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={`w-full justify-between bg-white font-normal ${contractForm.tipo_contrato === "transporte" ? (contractErrors.transportadora ? "border-red-500" : "") : (contractErrors.comprador ? "border-red-500" : "")}`}
                        >
                          {parceiroSelecionado
                            ? (parceiroSelecionado.nome_razao || parceiroSelecionado.razao_social || parceiroSelecionado.nome || parceiroSelecionado.cnpj_cpf)
                            : (contractForm.tipo_contrato === "transporte"
                              ? (contractForm.transportadora || "Selecione uma transportadora...")
                              : (contractForm.comprador || "Selecione um parceiro..."))}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[480px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Buscar parceiro..." value={partnerSearch} onValueChange={setPartnerSearch} />
                          <CommandList>
                            <CommandEmpty>Nenhum parceiro encontrado.</CommandEmpty>
                            <CommandGroup>
                              {parceirosFiltrados.map((p) => {
                                const pNome = (p.nome_razao || p.razao_social || p.nome || "").trim();
                                const pDoc = (p.cnpj_cpf || "").trim();
                                const pEmail = (p.email || "").trim();
                                const label = pNome || pDoc || "Parceiro";
                                const selectedName = contractForm.tipo_contrato === "transporte" ? contractForm.transportadora : contractForm.comprador;
                                return (
                                  <CommandItem
                                    key={p.id || label}
                                    value={label}
                                    keywords={[pNome, pDoc, pEmail, p.cidade, p.estado].filter(Boolean)}
                                    onSelect={() => handleSelectParceiro(p)}
                                  >
                                    <Check className={`mr-2 h-4 w-4 ${label === selectedName ? "opacity-100" : "opacity-0"}`} />
                                    <div className="flex flex-col">
                                      <span>{label}</span>
                                      {(pDoc || pEmail || p.cidade) && (
                                        <span className="text-xs text-stone-500">
                                          {[pDoc, pEmail, [p.cidade, p.estado].filter(Boolean).join("/")].filter(Boolean).join(" • ")}
                                        </span>
                                      )}
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {(contractForm.tipo_contrato === "transporte" ? contractErrors.transportadora : contractErrors.comprador) && (
                      <p className="text-xs text-red-500">
                        {contractForm.tipo_contrato === "transporte" ? contractErrors.transportadora : contractErrors.comprador}
                      </p>
                    )}
                    {parceiroSelecionado && (
                      <div className="text-xs text-stone-500">
                        {[parceiroSelecionado.cnpj_cpf, parceiroSelecionado.email, [parceiroSelecionado.cidade, parceiroSelecionado.estado].filter(Boolean).join("/")].filter(Boolean).join(" • ")}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Mercadoria e Quantidades</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Produto *</Label>
                    <Select value={contractForm.produto} onValueChange={(v) => setContractForm({ ...contractForm, produto: v })}>
                      <SelectTrigger className={contractErrors.produto ? "border-red-500" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(produtosConfig).map((key) => (
                          <SelectItem key={key} value={key}>{produtosConfig[key].icon} {produtosConfig[key].label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {contractErrors.produto && <p className="text-xs text-red-500">{contractErrors.produto}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Unidade</Label>
                    <Select value={contractForm.unidade} onValueChange={(v) => setContractForm({ ...contractForm, unidade: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sacas">Sacas</SelectItem>
                        <SelectItem value="kg">Kg</SelectItem>
                        <SelectItem value="ton">Toneladas</SelectItem>
                        <SelectItem value="litros">Litros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantidade Total *</Label>
                    <Input
                      inputMode="decimal"
                      value={contractForm.quantidade_total}
                      onChange={(e) => setContractForm({ ...contractForm, quantidade_total: e.target.value })}
                      onBlur={() => handleDecimalBlur("quantidade_total", 2)}
                      placeholder="0"
                      className={contractErrors.quantidade_total ? "border-red-500" : ""}
                    />
                    {contractErrors.quantidade_total && <p className="text-xs text-red-500">{contractErrors.quantidade_total}</p>}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Valores e Datas</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {contractForm.tipo_contrato === "compra_venda" && (
                    <div className="space-y-2">
                      <Label>Preço Contrato (R$) *</Label>
                      <Input
                        inputMode="decimal"
                        value={contractForm.preco_contrato}
                        onChange={(e) => setContractForm({ ...contractForm, preco_contrato: e.target.value })}
                        onBlur={() => handleDecimalBlur("preco_contrato", 2)}
                        placeholder="0,00"
                        className={contractErrors.preco_contrato ? "border-red-500" : ""}
                      />
                      {contractErrors.preco_contrato && <p className="text-xs text-red-500">{contractErrors.preco_contrato}</p>}
                    </div>
                  )}
                  {contractForm.tipo_contrato === "armazenagem" && (
                    <div className="space-y-2">
                      <Label>Tarifa Armazenagem (R$) *</Label>
                      <Input
                        inputMode="decimal"
                        value={contractForm.tarifa_armazenagem}
                        onChange={(e) => setContractForm({ ...contractForm, tarifa_armazenagem: e.target.value })}
                        onBlur={() => handleDecimalBlur("tarifa_armazenagem", 2)}
                        placeholder="0,00"
                        className={contractErrors.tarifa_armazenagem ? "border-red-500" : ""}
                      />
                      {contractErrors.tarifa_armazenagem && <p className="text-xs text-red-500">{contractErrors.tarifa_armazenagem}</p>}
                    </div>
                  )}
                  {contractForm.tipo_contrato === "transporte" && (
                    <div className="space-y-2">
                      <Label>Valor do Frete (R$) *</Label>
                      <Input
                        inputMode="decimal"
                        value={contractForm.valor_frete}
                        onChange={(e) => setContractForm({ ...contractForm, valor_frete: e.target.value })}
                        onBlur={() => handleDecimalBlur("valor_frete", 2)}
                        placeholder="0,00"
                        className={contractErrors.valor_frete ? "border-red-500" : ""}
                      />
                      {contractErrors.valor_frete && <p className="text-xs text-red-500">{contractErrors.valor_frete}</p>}
                    </div>
                  )}
                  {contractForm.tipo_contrato === "armazenagem" && (
                    <>
                      <div className="space-y-2">
                        <Label>Início *</Label>
                        <Input
                          type="date"
                          value={contractForm.data_inicio}
                          onChange={(e) => setContractForm({ ...contractForm, data_inicio: e.target.value })}
                          className={contractErrors.data_inicio ? "border-red-500" : ""}
                        />
                        {contractErrors.data_inicio && <p className="text-xs text-red-500">{contractErrors.data_inicio}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label>Fim</Label>
                        <Input
                          type="date"
                          value={contractForm.data_fim}
                          onChange={(e) => setContractForm({ ...contractForm, data_fim: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                  {(contractForm.tipo_contrato === "compra_venda" || contractForm.tipo_contrato === "transporte") && (
                    <div className="space-y-2">
                      <Label>Prazo de Entrega</Label>
                      <Input
                        type="date"
                        value={contractForm.prazo_entrega}
                        onChange={(e) => setContractForm({ ...contractForm, prazo_entrega: e.target.value })}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
              {contractForm.tipo_contrato === "armazenagem" && (
                <Card>
                  <CardHeader>
                    <CardTitle>Armazenagem</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Armazém *</Label>
                      <Select value={contractForm.armazem_id} onValueChange={(v) => setContractForm({ ...contractForm, armazem_id: v })}>
                        <SelectTrigger className={contractErrors.armazem_id ? "border-red-500" : ""}>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {armazens.map((arm) => (
                            <SelectItem key={arm.id} value={arm.id}>{arm.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {contractErrors.armazem_id && <p className="text-xs text-red-500">{contractErrors.armazem_id}</p>}
                    </div>
                  </CardContent>
                </Card>
              )}
              {contractForm.tipo_contrato === "transporte" && (
                <Card>
                  <CardHeader>
                    <CardTitle>Rota</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Origem *</Label>
                      <Input
                        value={contractForm.origem}
                        onChange={(e) => setContractForm({ ...contractForm, origem: e.target.value })}
                        placeholder="Ex: Fazenda Machado"
                        className={contractErrors.origem ? "border-red-500" : ""}
                      />
                      {contractErrors.origem && <p className="text-xs text-red-500">{contractErrors.origem}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Destino *</Label>
                      <Input
                        value={contractForm.destino}
                        onChange={(e) => setContractForm({ ...contractForm, destino: e.target.value })}
                        placeholder="Ex: Porto de Paranaguá"
                        className={contractErrors.destino ? "border-red-500" : ""}
                      />
                      {contractErrors.destino && <p className="text-xs text-red-500">{contractErrors.destino}</p>}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            <TabsContent value="termos" className="mt-4 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Termos Contratuais</Label>
                  <Textarea
                    rows={8}
                    value={contractForm.termos}
                    onChange={(e) => setContractForm({ ...contractForm, termos: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cláusulas Específicas</Label>
                  <Textarea
                    rows={8}
                    value={contractForm.clausulas}
                    onChange={(e) => setContractForm({ ...contractForm, clausulas: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Condições Comerciais</Label>
                <Textarea
                  rows={6}
                  value={contractForm.condicoes}
                  onChange={(e) => setContractForm({ ...contractForm, condicoes: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea
                  rows={4}
                  value={contractForm.observacoes}
                  onChange={(e) => setContractForm({ ...contractForm, observacoes: e.target.value })}
                />
              </div>
            </TabsContent>
            <TabsContent value="anexos" className="mt-4 space-y-4">
              {!editingContract ? (
                <div className="flex flex-col items-center justify-center p-10 text-center border border-dashed rounded-lg bg-stone-50">
                  <FileText className="w-10 h-10 text-stone-300 mb-3" />
                  <h3 className="text-lg font-medium text-stone-900">Salvar primeiro</h3>
                  <p className="text-stone-500 max-w-xs mx-auto mt-1">Salve o contrato para liberar o envio de anexos.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <Label>Anexar documento</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleUploadContratoAnexo(file);
                          e.target.value = "";
                        }}
                      />
                    </div>
                  </div>
                  {contractForm.anexos?.length ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {contractForm.anexos.map((doc, idx) => (
                        <div key={`${doc.url || doc.nome}-${idx}`} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{doc.nome}</p>
                            <p className="text-xs text-stone-500">{doc.tipo || "Arquivo"} • {doc.tamanho ? `${(doc.tamanho / 1024 / 1024).toFixed(2)} MB` : "-"}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleOpenAnexo(doc)}>Abrir</Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500"
                              onClick={() => setContractForm((prev) => ({ ...prev, anexos: (prev.anexos || []).filter((_, i) => i !== idx) }))}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500">Nenhum anexo enviado.</p>
                  )}
                </>
              )}
            </TabsContent>
            <TabsContent value="historico" className="mt-4 space-y-3">
              {contratoHistorico.length === 0 ? (
                <p className="text-sm text-stone-500">Nenhuma alteração registrada.</p>
              ) : (
                <div className="space-y-2">
                  {contratoHistorico.slice().reverse().map((item, idx) => (
                    <div key={`${item.data || idx}`} className="p-3 border rounded-lg bg-white">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{item.acao || "Atualização"}</span>
                        <span className="text-stone-500">{item.data ? new Date(item.data).toLocaleString("pt-BR") : "-"}</span>
                      </div>
                      <div className="text-xs text-stone-500 mt-1">{item.usuario || "Sistema"}</div>
                      {item.descricao && <div className="text-sm text-stone-600 mt-1">{item.descricao}</div>}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={() => { setContractDialogOpen(false); setEditingContract(null); resetContractForm(); }} className="flex-1">Cancelar</Button>
            <Button
              onClick={async () => {
                const errors = validateContractForm(contractForm);
                setContractErrors(errors);
                if (Object.keys(errors).length) return;
                const propriedadeId = localStorage.getItem("propriedade_selecionada");
                if (!propriedadeId) return;
                const descricao = buildContractDescricao(contractForm);
                const valor = buildContractValor(contractForm);
                const dataBase = contractForm.tipo_contrato === "armazenagem"
                  ? contractForm.data_inicio
                  : (contractForm.data_contrato || contractForm.data_inicio);
                const clienteNome = contractForm.tipo_contrato === "transporte" ? contractForm.transportadora : contractForm.comprador;
                const categoria = contractForm.tipo_contrato === "armazenagem"
                  ? "Contrato de Armazenagem"
                  : contractForm.tipo_contrato === "transporte"
                    ? "Contrato de Transporte"
                    : "Venda de Grãos (Contrato)";
                const prev = editingContract ? parseObservacoesContrato(editingContract.observacoes) : { meta: {} };
                const u = await agrogestao.auth.me();
                const historicoAtual = Array.isArray(prev.meta?.historico) ? prev.meta.historico : [];
                const historico = [
                  ...historicoAtual,
                  { data: new Date().toISOString(), usuario: u?.email || "", acao: editingContract ? "atualizado" : "criado", descricao }
                ];
                const meta = {
                  ...prev.meta,
                  codigo: contractForm.codigo,
                  tipo_contrato: contractForm.tipo_contrato,
                  operacao: contractForm.operacao,
                  produto: contractForm.produto,
                  comprador: contractForm.comprador,
                  comprador_id: contractForm.comprador_id,
                  quantidade_total: parseNumero(contractForm.quantidade_total),
                  unidade: contractForm.unidade,
                  preco_contrato: parseNumero(contractForm.preco_contrato),
                  data_contrato: contractForm.data_contrato,
                  data_inicio: contractForm.data_inicio,
                  data_fim: contractForm.data_fim,
                  tarifa_armazenagem: parseNumero(contractForm.tarifa_armazenagem),
                  armazem_id: contractForm.armazem_id,
                  origem: contractForm.origem,
                  destino: contractForm.destino,
                  transportadora: contractForm.transportadora,
                  transportadora_id: contractForm.transportadora_id,
                  valor_frete: parseNumero(contractForm.valor_frete),
                  prazo_entrega: contractForm.prazo_entrega,
                  termos: contractForm.termos,
                  clausulas: contractForm.clausulas,
                  condicoes: contractForm.condicoes,
                  anexos: contractForm.anexos || [],
                  historico
                };
                const observacoes = buildObservacoesContrato(contractForm.observacoes, meta);
                if (editingContract) {
                  await agrogestao.entities.ContaReceber.update(editingContract.id, {
                    descricao,
                    valor,
                    data_vencimento: dataBase,
                    cliente: clienteNome,
                    categoria,
                    observacoes
                  });
                } else {
                  await agrogestao.entities.ContaReceber.create({
                    propriedade_id: propriedadeId,
                    descricao,
                    valor,
                    data_vencimento: dataBase,
                    categoria,
                    cliente: clienteNome,
                    observacoes,
                    status: "agendado"
                  });
                  try {
                    localStorage.removeItem(contratoDraftKey);
                  } catch { }
                }
                setContractDialogOpen(false);
                setEditingContract(null);
                resetContractForm();
                queryClient.invalidateQueries({ queryKey: ["contas_receber"] });
              }}
              className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]"
            >
              {editingContract ? "Salvar alterações" : "Salvar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
